'use client';

import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useMount } from 'react-use';

import { MovableModalLegacy } from '@flipster/features/src/shared/movable-modal';
import { SymbolContext } from '@flipster/features/src/shared/symbol/bunja';
import { ModalErrorBoundary } from '@flipster/features/src/utils/modal-error-boundary';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import segment from '@flipster/utils/segment';
import { PageType, PositionEvent } from '@flipster/utils/segment/events/position';

import AdjustMarginConfirmButton from './AdjustMarginConfirmButton';
import AdjustMarginContent from './AdjustMarginContent';
import { AdjustMarginModalContext, TabType, adjustMarginStateBunja } from './state';

const Tabs = createTabsComponent<TabType>();

interface Props {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  slot: number;
  page: PageType;
  onSuccess: () => void;
  onExit?: () => void;
}

const AdjustMarginModalImpl = ({ isOpen, onClose, page, onSuccess, onExit }: Omit<Props, 'symbol' | 'slot'>) => {
  const transCommon = useTranslations('Common.r250401');
  const transPositionAddFunds = useTranslations('Position_add_funds.r250401');

  const { resetForm, tabAtom, currentPositionAtom } = useBunja(adjustMarginStateBunja);

  const [tab, setTab] = useAtom(tabAtom);
  const currentPosition = useAtomValue(currentPositionAtom);

  useMount(() => {
    segment.track(PositionEvent.AdjustMarginPageView);
  });

  return (
    <MovableModalLegacy.Root size='small' css={{ minH: 526 }} isOpen={isOpen} onClose={onClose} onExit={onExit}>
      <MovableModalLegacy.Header>
        <MovableModalLegacy.Title label={transPositionAddFunds('Adjust_margin')} />
      </MovableModalLegacy.Header>
      <MovableModalLegacy.Content>
        <Tabs
          type='fixed'
          value={tab}
          onClick={(t) => {
            setTab(t);
            resetForm();
          }}
        >
          <Tabs.Tab value='add' label={transCommon('Add')} />
          <Tabs.Tab value='remove' label={transCommon('Remove')} />
        </Tabs>
        <AdjustMarginContent currentPosition={currentPosition} page={page} />
      </MovableModalLegacy.Content>
      <MovableModalLegacy.Footer>
        <AdjustMarginConfirmButton
          currentPosition={currentPosition}
          page={page}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      </MovableModalLegacy.Footer>
    </MovableModalLegacy.Root>
  );
};

const AdjustMarginModal = ({ isOpen, onClose, symbol, slot, page, onSuccess, onExit }: Props) => {
  return (
    <SymbolContext value={symbol}>
      <AdjustMarginModalContext.Provider value={{ symbol, slot }}>
        <ModalErrorBoundary onError={onClose}>
          <AdjustMarginModalImpl page={page} onSuccess={onSuccess} isOpen={isOpen} onClose={onClose} onExit={onExit} />
        </ModalErrorBoundary>
      </AdjustMarginModalContext.Provider>
    </SymbolContext>
  );
};

export default AdjustMarginModal;
